import { useState } from "react";
import { InstagramLogo, FacebookLogo, LinkedinLogo, ArrowUp, X } from "@phosphor-icons/react";
import { scrollToId } from "@/lib/scroll";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const QUICK = [
  { label: "Home", id: "home" },
  { label: "Prices", id: "pricing" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];
const SERVICES = ["Websites & Maintenance", "Print Design", "Branding & Logos", "Social Media Content", "Google Setup"];

const TERMS = {
  title: "Terms & Conditions",
  intro: "MM Digital Agency – Terms & Conditions",
  points: [
    "A 50% deposit is required before work begins.",
    "Remaining payment is due before website launch.",
    "Two rounds of revisions are included.",
    "Additional revisions are billed separately.",
    "Delays caused by missing client content may extend delivery times.",
    "The client is responsible for supplying accurate information.",
    "Hosting, domain names and third-party subscriptions are billed separately unless included in writing.",
    "Monthly maintenance is billed in advance each month.",
    "Maintenance may be cancelled with 30 days' written notice.",
    "Late payments may result in suspension of services.",
    "MM Digital Agency retains the right to display completed work in its portfolio unless otherwise agreed.",
  ],
};

const PRIVACY = {
  title: "Privacy Policy",
  intro: "MM Digital Agency respects your privacy.",
  sections: [
    {
      heading: "Information we may collect",
      items: ["Name", "Email address", "Phone number", "Business details", "Website information", "Project files"],
    },
    {
      heading: "How we use this information",
      items: ["Provide website design services", "Communicate regarding projects", "Send invoices", "Provide support"],
    },
  ],
  paragraphs: [
    "We do not sell personal information.",
    "Information may be shared only with trusted service providers such as hosting providers, domain registrars and payment processors where necessary to complete your project.",
    "Reasonable security measures are used to protect your information.",
    "Clients may request access to or correction of their personal information by contacting MM Digital Agency.",
  ],
};

export default function Footer() {
  const [legal, setLegal] = useState(null); // 'terms' | 'privacy' | null
  return (
    <footer className="bg-white text-ink border-t border-ink/10 pt-20 pb-8" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-12 gap-12 pb-16">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <span className="font-display text-2xl font-extrabold tracking-tighter">MM</span>
              <div className="leading-none">
                <div className="font-display text-sm font-bold tracking-[0.2em]">DIGITAL</div>
                <div className="font-mono text-[9px] tracking-[0.45em] text-gold">AGENCY</div>
              </div>
            </div>
            <p className="text-ink/50 text-sm leading-relaxed max-w-xs">
              We create beautiful, high-performing websites that help businesses stand out and grow online.
            </p>
            <div className="flex gap-3 mt-6">
              {[InstagramLogo, FacebookLogo, LinkedinLogo].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="w-10 h-10 rounded-full border border-ink/20 flex items-center justify-center hover:bg-gold hover:text-ink hover:border-gold transition-colors"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 md:col-start-6">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {QUICK.map((q) => (
                <li key={q.id}>
                  <button onClick={() => scrollToId(q.id)} data-testid={`footer-link-${q.id}`} className="text-sm text-ink/60 hover:text-ink transition-colors">
                    {q.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-5">Services</h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s} className="text-sm text-ink/60">{s}</li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-5">Contact</h4>
            <ul className="space-y-3 text-sm text-ink/60">
              <li>+61 433 784 209</li>
              <li className="break-all">mmdigitalagency05@gmail.com</li>
              <li>Melbourne, Australia</li>
              <li className="pt-3 mt-3 border-t border-ink/10">
                <span className="font-display font-bold text-ink block">Merone Mekonnen</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-deep">Digital Designer · Founder</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-ink/10">
          <p className="font-mono text-[11px] text-ink/40 tracking-wide">© 2026 MM Digital Agency. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <button
              data-testid="footer-privacy-link"
              onClick={() => setLegal("privacy")}
              className="font-mono text-[11px] text-ink/40 hover:text-gold transition-colors"
            >
              Privacy Policy
            </button>
            <button
              data-testid="footer-terms-link"
              onClick={() => setLegal("terms")}
              className="font-mono text-[11px] text-ink/40 hover:text-gold transition-colors"
            >
              Terms &amp; Conditions
            </button>
            <button
              onClick={() => scrollToId("home")}
              data-testid="footer-back-to-top"
              className="w-10 h-10 rounded-full border border-ink/20 flex items-center justify-center hover:bg-gold hover:text-ink hover:border-gold transition-colors"
            >
              <ArrowUp size={16} />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={legal !== null} onOpenChange={(o) => !o && setLegal(null)}>
        <DialogContent
          className="max-w-2xl bg-white border border-ink/15 text-ink p-0 rounded-[3px] max-h-[85vh] overflow-hidden [&>button]:hidden"
          data-testid="legal-dialog"
        >
          <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
            <DialogTitle className="font-display font-bold text-2xl">
              {legal === "terms" ? TERMS.title : PRIVACY.title}
            </DialogTitle>
            <button
              onClick={() => setLegal(null)}
              data-testid="legal-dialog-close"
              className="w-9 h-9 rounded-full border border-ink/20 flex items-center justify-center hover:bg-gold hover:text-ink hover:border-gold transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <div className="px-8 py-7 overflow-y-auto space-y-5 text-ink/75 text-sm leading-relaxed" style={{ maxHeight: "70vh" }}>
            {legal === "terms" && (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{TERMS.intro}</p>
                <ul className="space-y-3 list-none">
                  {TERMS.points.map((p, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="font-mono text-gold shrink-0 text-xs pt-1">{String(i + 1).padStart(2, "0")}</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {legal === "privacy" && (
              <>
                <p className="text-ink/85">{PRIVACY.intro}</p>
                {PRIVACY.sections.map((s) => (
                  <div key={s.heading}>
                    <h4 className="font-display font-bold text-base text-ink mb-3">{s.heading}</h4>
                    <ul className="space-y-2 list-none">
                      {s.items.map((it) => (
                        <li key={it} className="flex gap-3">
                          <span className="text-gold shrink-0">·</span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {PRIVACY.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
